import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';


const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 15px 50px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 10px 30px;
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem;
  letter-spacing: 5px;
  margin: 40px 0;
  position: relative;
  padding: 25px 0;
  font-weight: 400;
  text-transform: uppercase;
  
  &::before, &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 280px;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    letter-spacing: 3px;
    margin: 30px 0;
    padding: 20px 0;
    
    &::before, &::after {
      width: 220px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    letter-spacing: 2px;
    
    &::before, &::after {
      width: 180px;
    }
  }
`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 2.4rem;
  letter-spacing: 1px;
  font-style: normal;
  text-transform: none;
  margin-top: 15px;
  color: #b38b6d;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const IntroText = styled.p`
  text-align: center;
  margin-bottom: 30px;
  line-height: 1.6;
  color: #666;
  font-size: 1.1rem;
`;

const PollContainer = styled.div`
  width: 100%;
  max-width: 600px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 25px;
  margin-bottom: 30px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 15px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    width: 100%;
  }
`;

const PollTitle = styled.h2`
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.8rem;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const PollDescription = styled.p`
  text-align: center;
  margin-bottom: 25px;
  color: #666;
`;

const SliderContainer = styled.div`
  width: 100%;
  margin: 30px 0;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    margin: 20px 0;
  }
`;

const SliderWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const SliderValue = styled.span`
  font-size: 1.2rem;
  color: #947060;
  font-weight: 600;
  width: 40px;
  text-align: center;
`;

const StyledSlider = styled.input`
  flex-grow: 1;
  height: 7px;
  border-radius: 5px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #947060;
    cursor: pointer;
    transition: all 0.15s ease;
    
    &:hover {
      transform: scale(1.1);
      background: #b38b6d;
    }
  }
  
  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #947060;
    cursor: pointer;
    border: none;
    transition: all 0.15s ease;
    
    &:hover {
      transform: scale(1.1);
      background: #b38b6d;
    }
  }
`;

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  color: #777;
  font-size: 0.85rem;
  padding: 0 5px;
`;

const Button = styled.button`
  background-color: #947060;
  color: #fff;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.1rem;
  padding: 12px 25px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.2s ease;
  display: block;
  width: 100%;
  max-width: 200px;
  margin: 30px auto 0;
  
  &:hover {
    background-color: #b38b6d;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  margin-top: 15px;
  text-align: center;
  padding: 10px;
  border-radius: 4px;
  color: ${props => props.isError ? '#721c24' : '#155724'};
  background-color: ${props => props.isError ? '#f8d7da' : '#d4edda'};
  border: 1px solid ${props => props.isError ? '#f5c6cb' : '#c3e6cb'};
  display: ${props => props.visible ? 'block' : 'none'};
`;

const GraphContainer = styled.div`
  margin-top: 30px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    margin-top: 20px;
  }
`;

const ResultsTitle = styled.h3`
  font-size: 1.4rem;
  color: #947060;
  margin-bottom: 15px;
  text-align: center;
  font-weight: normal;
  font-family: 'Cormorant Garamond', serif;
`;

const GraphBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
  }
`;

const BarLabel = styled.div`
  min-width: 50px;
  font-size: 0.9rem;
  color: #666;
  text-align: right;
  margin-right: 10px;
  
  @media (max-width: 480px) {
    min-width: 35px;
    font-size: 0.8rem;
    margin-right: 5px;
  }
`;

const BarContainer = styled.div`
  flex-grow: 1;
  height: 25px;
  background-color: #eee;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const Bar = styled.div`
  height: 100%;
  width: ${props => props.percentage}%;
  background-color: #b38b6d;
  transition: width 0.5s ease;
`;

const BarValue = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.85rem;
  color: ${props => props.percentage > 50 ? '#fff' : '#666'};
  font-weight: 500;
`;

const BinaryPollContainer = styled.div`
  width: 100%;
  margin: 30px 0;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    margin: 20px 0;
  }
`;

const RadioOptionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 20px 0;
  padding: 10px 0;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const RadioOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 45%;
  
  @media (max-width: 480px) {
    width: 80%;
    margin-bottom: 30px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const RadioButton = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border: 2px solid #947060;
  border-radius: 50%;
  outline: none;
  cursor: pointer;
  position: relative;
  margin-bottom: 10px;

  &:checked {
    background-color: #947060;
    border: 2px solid #947060;
  }

  &:checked::before {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    background-color: white;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:disabled {
    border-color: #cccccc;
    background-color: ${props => props.checked ? '#cccccc' : 'transparent'};
    cursor: not-allowed;
  }
`;

const RadioLabel = styled.label`
  font-size: 1.1rem;
  color: #333;
  text-align: center;
  font-weight: 500;
`;

const BinaryProgressBar = styled.div`
  width: 90%;
  height: 20px;
  background-color: #eee;
  border-radius: 10px;
  overflow: hidden;
  margin: 20px auto 10px;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
  
  @media (max-width: 480px) {
    width: 100%;
    margin: 10px auto 5px;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 15px 0;
  padding-right: 10px; // Add padding to contain child elements
  box-sizing: border-box;
`;

const RadioItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 10px 15px;
  background-color: ${props => props.checked ? 'rgba(179, 139, 109, 0.1)' : 'transparent'};
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(179, 139, 109, 0.05);
  }
`;

const RadioIcon = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 22px;
  height: 22px;
  border: 2px solid #947060;
  border-radius: 50%;
  outline: none;
  margin-right: 12px;
  cursor: pointer;
  position: relative;

  &:checked {
    background-color: #947060;
    border: 2px solid #947060;
  }

  &:checked::before {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    background-color: white;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:disabled {
    border-color: #cccccc;
    background-color: ${props => props.checked ? '#cccccc' : 'transparent'};
    cursor: not-allowed;
  }
`;

const RadioText = styled.label`
  font-size: 1rem;
  color: #333;
  cursor: pointer;
  flex-grow: 1;
`;

const OtherInput = styled.input`
  width: 60%;  // Start with a smaller width
  max-width: 200px; // Lower the maximum width
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  margin-left: 34px;
  margin-right: 20px;
  margin-bottom: 8px;
  font-size: 0.85rem;
  box-sizing: border-box;
  display: block;
  overflow: hidden; // Prevent overflow
  
  &:focus {
    border-color: #947060;
    outline: none;
    box-shadow: 0 0 0 2px rgba(148, 112, 96, 0.1);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    width: 60%;
    max-width: 180px;
    margin-left: 28px;
    padding: 5px 6px;
    font-size: 0.8rem;
  }
`;

const ResultItem = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
  width: 100%;
`;

const ResultLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
`;

const ResultText = styled.span`
  font-size: 0.9rem;
  color: #555;
`;

const ResultPercentage = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: #947060;
`;

const ERIKA_COLOR = '#f8c9c4'; // Light pink for Erika
const ANDREW_COLOR = '#acc8d7'; // Light blue for Andrew

const ProgressIndicator = styled.div`
  height: 100%;
  width: 100%; /* Ensure width is 100% */
  background: linear-gradient(to right, 
    ${ERIKA_COLOR} 0%, 
    ${ERIKA_COLOR} ${props => props.leftPercent}%, 
    ${ANDREW_COLOR} ${props => props.leftPercent}%, 
    ${ANDREW_COLOR} 100%);
  transition: all 0.5s ease;
`;

const ProgressLabels = styled.div`
  display: flex;
  justify-content: space-between;
  width: 90%;
  margin: 0 auto;
  font-size: 0.8rem;
  color: #666;
  
  @media (max-width: 480px) {
    width: 100%;
    font-size: 0.75rem;
  }
`;

const ProgressLabel = styled.div`
  font-weight: bold;
  color: ${props => props.isMajority ? '#5a7b96' : '#947060'};
`;

const ProfileImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: #f0f0f0;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  margin-bottom: 15px;
  border: 3px solid ${props => props.isSelected ? '#947060' : 'transparent'};
  transition: border-color 0.3s ease;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;


// Update the LiveOdds component to include the binary poll
const LiveOdds = () => {
    const { currentUser } = useAuth();
    const [polls, setPolls] = useState([
        {
            id: 'first_cry',
            title: 'Who will cry first during the ceremony?',
            description: 'Which one of the happy couple will shed the first tear?',
            poll_type: 'binary',
            options: ['option1', 'option2'],
            optionLabels: ['Erika', 'Andrew'],
            optionImages: ['/img/erika-cry.png', '/img/andrew-cry.png'],
            value: 'option1',
            results: null,
            submitted: false,
            loading: false,
            status: { message: '', error: false, visible: false }
        },
        {
            id: 'crying',
            title: 'How many times will Erika cry?',
            description: 'Place your bet on how many times Erika will cry during the wedding day!',
            poll_type: 'incremental',
            options: Array.from({ length: 11 }, (_, i) => i === 10 ? '10+' : String(i)),
            value: 0,
            results: null,
            submitted: false,
            loading: false,
            status: { message: '', error: false, visible: false }
        },
        {
            id: 'wedding_hashtag',
            title: 'What should our wedding hashtag be?',
            description: 'Help us pick the perfect hashtag for our social media memories!',
            poll_type: 'multiple_choice',
            options: [
                'yoonwang', 
                'wangyoon', 
                'yoonandwang', 
                'foreverwang',
                'wanderful',
                'other'
            ],
            optionLabels: [
                '#Can\'tWangToBeWithYoon', 
                '#WangderfulYoonion', 
                '#WangYoonited', 
                '#ErikaFoundHerWang',
                'YooniqueWangderland',
                'Suggest Your Own'
            ],
            value: 'yoonwang',
            otherValue: '',
            results: null,
            submitted: false,
            loading: false,
            status: { message: '', error: false, visible: false }
        },
        {
            id: 'andrew_before',
            title: 'What will Andrew be doing 30 minutes before the ceremony?',
            description: 'Pick the most likely scenario for the groom right before the big moment',
            poll_type: 'multiple_choice',
            options: [
                'calm_collected', 
                'tying_tie', 
                'hyping_crew', 
                'looking_shoes',
                'other'
            ],
            optionLabels: [
                'Calm and collected', 
                'Frantically tying a tie', 
                'Taking shots with the boys', 
                'Asking where his shoes are',
                'Other'
            ],
            value: 'calm_collected',
            otherValue: '',
            results: null,
            submitted: false,
            loading: false,
            status: { message: '', error: false, visible: false }
        },
        {
            id: 'erika_before',
            title: 'What will Erika be doing 30 minutes before the ceremony?',
            description: 'What do you think the bride will be up to right before walking down the aisle?',
            poll_type: 'multiple_choice',
            options: [
                'laughing', 
                'makeup', 
                'crying', 
                'timeline',
                'other'
            ],
            optionLabels: [
                'Laughing with her besties', 
                'One last touch-up on the makeup', 
                'Crying happy tears', 
                'Triple-checking the timeline, is she a Bridezilla?',
                'Other'
            ],
            value: 'laughing',
            otherValue: '',
            results: null,
            submitted: false,
            loading: false,
            status: { message: '', error: false, visible: false }
        },
        {
            id: 'first_dancer',
            title: 'Who will be the first one on the dance floor?',
            description: 'After the first dance, who will get the party started?',
            poll_type: 'multiple_choice',
            options: [
                'andrew', 
                'erika', 
                'guest', 
                'dj',
                'paul'
            ],
            optionLabels: [
                'Andrew', 
                'Erika', 
                'A guest dragging them out', 
                'Best man/Maid of Honor',
                'Paul Lee'
            ],
            value: 'andrew',
            otherValue: '',
            results: null,
            submitted: false,
            loading: false,
            status: { message: '', error: false, visible: false }
        },
        {
            id: 'erika_laugh',
            title: 'What\'s most likely to make Erika laugh mid-ceremony?',
            description: 'What will break Erika\'s composure during the vows?',
            poll_type: 'multiple_choice',
            options: [
                'face', 
                'joke', 
                'kid', 
                'crying',
                'other'
            ],
            optionLabels: [
                'Andrew\'s face', 
                'A private inside joke', 
                'A rogue flower girl/ring bearer moment', 
                'Trying not to cry and failing',
                'Other'
            ],
            value: 'face',
            otherValue: '',
            results: null,
            submitted: false,
            loading: false,
            status: { message: '', error: false, visible: false }
        },
        {
            id: 'andrew_cry',
            title: 'How long will Andrew\'s happy-cry face last during the ceremony?',
            description: 'Predict the groom\'s emotional state during the vows',
            poll_type: 'multiple_choice',
            options: [
                'single_tear', 
                'waterworks', 
                'red_eyes', 
                'composed',
                'other'
            ],
            optionLabels: [
                'A single tear, movie-style', 
                'Full waterworks', 
                'Just red eyes, no tears', 
                'He\'ll hold it together… barely',
                'Other'
            ],
            value: 'single_tear',
            otherValue: '',
            results: null,
            submitted: false,
            loading: false,
            status: { message: '', error: false, visible: false }
        }
    ]);
  
  
  // Add missing calculatePercentage function
  const calculatePercentage = (pollResults, option) => {
    if (!pollResults) return 0;
    
    const optionCount = pollResults[option] || 0;
    const total = Object.values(pollResults).reduce((sum, count) => sum + count, 0);
    
    return total > 0 ? Math.round((optionCount / total) * 100) : 0;
  };


  
  // Add missing handleSliderChange function
  const handleSliderChange = (pollIndex, value) => {
    setPolls(prevPolls => {
      const newPolls = [...prevPolls];
      newPolls[pollIndex].value = value;
      return newPolls;
    });
  };
  useEffect(() => {
    const fetchPollResults = async () => {
      try {
        const response = await axios.get('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/poll');
        
        if (response.data && response.data.polls) {
          setPolls(prevPolls => {
            return prevPolls.map(poll => {
              const fetchedPoll = response.data.polls.find(p => p.id === poll.id);
              if (fetchedPoll) {
                return {
                  ...poll,
                  results: fetchedPoll.results
                };
              }
              return poll;
            });
          });
        }
      } catch (error) {
        console.error('Failed to fetch poll results:', error);
      }
    };
    
    fetchPollResults();
  }, []);
  
  const handleBinaryOptionChange = (pollIndex, option) => {
    setPolls(prevPolls => {
      const newPolls = [...prevPolls];
      newPolls[pollIndex].value = option;
      return newPolls;
    });
  };
  
  const handleMultipleChoiceChange = (pollIndex, option) => {
    setPolls(prevPolls => {
      const newPolls = [...prevPolls];
      newPolls[pollIndex].value = option;
      return newPolls;
    });
  };

  const handleOtherInputChange = (pollIndex, value) => {
    setPolls(prevPolls => {
      const newPolls = [...prevPolls];
      newPolls[pollIndex].otherValue = value;
      return newPolls;
    });
  };

  // Update your submitPoll function to handle both types of polls
  const submitPoll = async (pollIndex) => {
    const poll = polls[pollIndex];
    
    // Set loading state for this poll only
    setPolls(prevPolls => {
      const newPolls = [...prevPolls];
      newPolls[pollIndex].loading = true;
      newPolls[pollIndex].status = { message: '', error: false, visible: false };
      return newPolls;
    });
    
    try {
      // Format the option value based on poll type
      let optionValue;
      let otherText = null;
      if (poll.poll_type === 'incremental') {
        optionValue = poll.options[poll.value];
      } else if (poll.poll_type === 'binary') { 
        optionValue = poll.value; // option1 or option2
      } else if (poll.poll_type === 'multiple_choice') {
        optionValue = poll.value;
        
        // If "other" is selected, make sure there's text
        if (poll.value === 'other') {
          if (!poll.otherValue || poll.otherValue.trim() === '') {
            // Show error if other is selected but no text is provided
            setPolls(prevPolls => {
              const newPolls = [...prevPolls];
              newPolls[pollIndex].loading = false;
              newPolls[pollIndex].status = { 
                message: 'Please enter text for your "Other" response', 
                error: true,
                visible: true 
              };
              return newPolls;
            });
            return; // Exit early
          }
          otherText = poll.otherValue.trim();
        }
      }

      const userEmail = currentUser?.email || 'anonymous@guest.com';
      
      
      const requestData = {
        id: poll.id,
        option: optionValue,
        poll_type: poll.poll_type,
        email: userEmail
      };

      if (otherText) {
        requestData.other_text = otherText;
      }
      console.log("Sending poll data:", requestData);
  
      const response = await axios.post(
        'https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/poll', 
        requestData
      );
      
      if (response.data && response.data.results) {
        // Update the poll results in state
        setPolls(prevPolls => {
          const newPolls = [...prevPolls];
          newPolls[pollIndex].results = response.data.results;
          newPolls[pollIndex].submitted = true;
          newPolls[pollIndex].loading = false;
          newPolls[pollIndex].status = { 
            message: 'Your prediction has been recorded!', 
            error: false,
            visible: true 
          };
          return newPolls;
        });
      }
    } catch (error) {
      console.error('Error submitting poll:', error);
      
      // Update error state for this poll only
      setPolls(prevPolls => {
        const newPolls = [...prevPolls];
        newPolls[pollIndex].loading = false;
        newPolls[pollIndex].status = { 
          message: 'Failed to submit your prediction. Please try again later.', 
          error: true,
          visible: true 
        };
        return newPolls;
      });
    } 
    
    // Clear the message after a few seconds
    setTimeout(() => {
      setPolls(prevPolls => {
        const newPolls = [...prevPolls];
        newPolls[pollIndex].status = { 
          message: '', 
          error: false,
          visible: false 
        };
        return newPolls;
      });

      setTimeout(() => {
        setPolls(prevPolls => {
          const newPolls = [...prevPolls];
          newPolls[pollIndex].submitted = false;
          return newPolls;
        });
      }, 500); // Small delay after the message fades
      
    }, 3000);
  };

  
  // Render method updates - modified to include both poll types
  return (
    <>
      <Helmet>
        <title>Wedding Predictions | Erika and Andrew's Wedding</title>
      </Helmet>
      <PageContainer>
        <PageTitle>
          Wedding Predictions
          <NameSpan>Erika & Andrew</NameSpan>
        </PageTitle>
        
        <IntroText>
          Join in the fun by making predictions about our wedding day! 
          Submit your guesses and see what others think will happen.
        </IntroText>
        
        {polls.map((poll, pollIndex) => (
          <PollContainer key={poll.id}>
            <PollTitle>{poll.title}</PollTitle>
            <PollDescription>{poll.description}</PollDescription>
            
            {poll.poll_type === 'incremental' ? (
              // Incremental poll UI (existing)
              <SliderContainer>
                <SliderWrapper>
                  <SliderValue>{poll.options[poll.value]}</SliderValue>
                  <StyledSlider 
                    type="range" 
                    min="0" 
                    max={poll.options.length - 1} 
                    value={poll.value}
                    onChange={(e) => handleSliderChange(pollIndex, parseInt(e.target.value))}
                    disabled={poll.submitted}
                  />
                </SliderWrapper>
                
                <SliderLabels>
                  <span>{poll.options[0]}</span>
                  <span>{poll.options[Math.floor(poll.options.length / 2)]}</span>
                  <span>{poll.options[poll.options.length - 1]}</span>
                </SliderLabels>
              </SliderContainer>
            ) : poll.poll_type === 'binary' ? (
              // Binary poll UI (new)
              <BinaryPollContainer>
              <RadioOptionsContainer>
                {/* Left option - Erika */}
                <RadioOption>
                  <ProfileImage 
                    src={poll.optionImages?.[0] || ''} 
                    isSelected={poll.value === 'option1'}
                  />
                  <RadioButton 
                    type="radio"
                    name={`poll_${poll.id}`}
                    checked={poll.value === 'option1'}
                    onChange={() => handleBinaryOptionChange(pollIndex, 'option1')}
                    disabled={poll.submitted}
                  />
                  <RadioLabel>{poll.optionLabels[0]}</RadioLabel>
                  {poll.results && (
                    <ProgressLabel 
                      isMajority={
                        calculatePercentage(poll.results, 'option1') >= 
                        calculatePercentage(poll.results, 'option2')
                      }
                      isErika={true}
                    >
                      {calculatePercentage(poll.results, 'option1')}%
                    </ProgressLabel>
                  )}
                </RadioOption>
                
                {/* Right option - Andrew */}
                <RadioOption>
                  <ProfileImage 
                    src={poll.optionImages?.[1] || ''} 
                    isSelected={poll.value === 'option2'}
                  />
                  <RadioButton 
                    type="radio"
                    name={`poll_${poll.id}`}
                    checked={poll.value === 'option2'}
                    onChange={() => handleBinaryOptionChange(pollIndex, 'option2')}
                    disabled={poll.submitted}
                  />
                  <RadioLabel>{poll.optionLabels[1]}</RadioLabel>
                  {poll.results && (
                    <ProgressLabel 
                      isMajority={
                        calculatePercentage(poll.results, 'option2') > 
                        calculatePercentage(poll.results, 'option1')
                      }
                      isErika={false}
                    >
                      {calculatePercentage(poll.results, 'option2')}%
                    </ProgressLabel>
                  )}
                </RadioOption>
              </RadioOptionsContainer>
              
              {/* Progress bar now below the options instead of overlapping */}
              {poll.results && (
                <BinaryProgressBar>
                  <ProgressIndicator 
                    leftPercent={calculatePercentage(poll.results, 'option1')}
                  />
                </BinaryProgressBar>
              )}
            </BinaryPollContainer>
            ) : (
                // Multiple choice poll UI (new)
                <RadioGroup>
                  {poll.options.map((option, i) => (
                    <React.Fragment key={option}>
                      <RadioItem checked={poll.value === option}>
                        <RadioIcon 
                          type="radio"
                          name={`poll_${poll.id}`}
                          checked={poll.value === option}
                          onChange={() => handleMultipleChoiceChange(pollIndex, option)}
                          disabled={poll.submitted}
                        />
                        <RadioText 
                          onClick={() => !poll.submitted && handleMultipleChoiceChange(pollIndex, option)}
                        >
                          {poll.optionLabels[i]}
                        </RadioText>
                      </RadioItem>
                      
                      {/* Add text input for "Other" option */}
                      {option === 'other' && poll.value === 'other' && (
                        <OtherInput
                          type="text"
                          placeholder="Please specify..."
                          value={poll.otherValue}
                          onChange={(e) => handleOtherInputChange(pollIndex, e.target.value)}
                          disabled={poll.submitted}
                          required
                        />
                      )}
                    </React.Fragment>
                  ))}
                </RadioGroup>
              )}
            
            {!poll.submitted && (
              <Button 
                onClick={() => submitPoll(pollIndex)} 
                disabled={poll.loading}
              >
                {poll.loading ? 'Submitting...' : 'Submit Prediction'}
              </Button>
            )}
            
            {poll.status.visible && (
                <StatusMessage isError={poll.status.error} visible={poll.status.visible}>
                    {poll.status.message}
                </StatusMessage>
            )}
            
            {poll.results && poll.poll_type === 'incremental' && (
              <GraphContainer>
                <ResultsTitle>Current Predictions</ResultsTitle>
                
                {poll.options.map((option, i) => {
                  const percentage = calculatePercentage(poll.results, option);
                  return (
                    <GraphBar key={i}>
                      <BarLabel>{option}</BarLabel>
                      <BarContainer>
                        <Bar percentage={percentage} />
                        <BarValue percentage={percentage}>
                        {percentage}%
                        </BarValue>
                      </BarContainer>
                    </GraphBar>
                  );
                })}
              </GraphContainer>
            )}
            {poll.results && poll.poll_type === 'multiple_choice' && (
                <GraphContainer>
                    <ResultsTitle>Current Predictions</ResultsTitle>
                    
                    {poll.options.map((option, i) => {
                    const percentage = calculatePercentage(poll.results, option);
                    if (percentage > 0) {
                        return (
                        <ResultItem key={option}>
                            <ResultLabel>
                            <ResultText>{poll.optionLabels[i]}</ResultText>
                            <ResultPercentage>{percentage}%</ResultPercentage>
                            </ResultLabel>
                            <BarContainer>
                            <Bar percentage={percentage} />
                            </BarContainer>
                        </ResultItem>
                        );
                    }
                    return null;
                    })}
                    
                    {/* Show any custom "Other" responses if available */}
                    {poll.results && poll.results.other_responses && poll.results.other_responses.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                        <h4 style={{ fontSize: '1rem', color: '#666' }}>Other responses:</h4>
                        <ul style={{ marginLeft: '20px', color: '#555' }}>
                        {poll.results.other_responses.map((response, i) => (
                            <li key={i}>{response}</li>
                        ))}
                        </ul>
                    </div>
                    )}
                </GraphContainer>
                )}
          </PollContainer>
        ))}
      </PageContainer>
    </>
  );
};

export default LiveOdds;